/**
 * Document Service
 * 
 * This service handles all document-related operations in Firestore,
 * implementing the complete document metadata structure as specified
 * in the AI-Powered Document Scanning and Auto-Fill documentation.
 * 
 * Features:
 * - Complete document metadata structure with AI processing support
 * - Multi-category classification with confidence scoring
 * - Connection tracking for entity associations
 * - Storage path management and download URL handling
 * - Processing status tracking and error handling
 * 
 * Database Structure:
 * users/{userId}/trips/{tripId}/documents/{docId}
 */

// Import Firebase db and storage directly from firebaseInit, matching the working pattern in other packages
import {
  db,
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc as deleteDocFS,
  query,
  orderBy,
  where,
  writeBatch,
  serverTimestamp
} from '@trip-planner-monorepo/shared/firebase/firebaseInit';
import { eventBridgeService, EVENT_TYPES } from '@trip-planner-monorepo/shared/services/eventService';
import { linkRegistryConfigService } from '@trip-planner-monorepo/shared/services/linkRegistryConfigService';
import { LINK_TYPES } from '@trip-planner-monorepo/shared/config/entityTypeRegistry';
import { shouldRouteThroughRegistry } from '@trip-planner-monorepo/shared/utils/linkRegistryModeUtils';
import { isActiveLink } from '@trip-planner-monorepo/shared/utils/linkCanonicalUtils';
import { createDocumentReferenceBlock } from '@trip-planner-monorepo/shared/utils/contentBlockUtils';
import { toRegistryEntityType } from '../utils/associationEntityTypes';
import { configService } from './configService';
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from '../constants/fileValidation';

// Ensure Firebase is properly initialized
const ensureFirebaseInitialized = () => {
  if (!db) {
    throw new Error('Firestore database not initialized. Please check Firebase configuration.');
  }
  if (!storage) {
    throw new Error('Firebase Storage not initialized. Please check Firebase configuration.');
  }
};

/**
 * Sanitizes filename to remove forbidden characters that cause issues on Windows/storage
 * Forbidden characters: : * ? " < > | \
 * @param {string} filename - Original filename
 * @returns {string} Sanitized filename safe for storage and export
 */
const sanitizeFilename = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return 'document';
  }

  return filename
    .replace(/[:*?"<>|\\]/g, '-')  // Replace forbidden characters with dash
    .replace(/\s+/g, ' ')          // Normalize multiple whitespace to single space
    .replace(/^[-\s]+|[-\s]+$/g, '') // Trim leading/trailing dashes and spaces
    .trim() || 'document';         // Fallback if result is empty
};

/**
 * Document Service Object
 * Provides comprehensive document management functionality
 */
export const documentService = {
  /**
   * Creates a new document with complete metadata structure
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {File} file - The file to upload
   * @param {Object} options - Additional options for document creation
   * @returns {Promise<Object>} The created document with complete metadata
   */
  async createDocument(userId, tripId, file, options = {}) {
    try {
      ensureFirebaseInitialized();
      // File type validation (server-side)
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        throw new Error('Unsupported file type.');
      }
      // File size validation (server-side)
      if (file.size > MAX_FILE_SIZE_BYTES) {
        throw new Error('File is too large. Maximum allowed size is 50MB.');
      }
      // Sanitize filename for storage and export safety
      const sanitizedFilename = sanitizeFilename(file.name);

      // Generate unique storage ID for storage path only
      const storageId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const storagePath = `users/${userId}/trips/${tripId}/documents/${storageId}/${sanitizedFilename}`;
      // DEBUG: Check storagePath before using ref
      console.log('[DEBUG] storagePath:', storagePath, 'typeof:', typeof storagePath);
      console.log('[DEBUG] Original filename:', file.name, 'â†’ Sanitized:', sanitizedFilename);
      const storageRef = ref(storage, storagePath);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      // Create document metadata structure
      const documentData = {
        name: sanitizedFilename,
        title: options.title || file.name, // Will be updated by AI processing, keep original for AI context
        originalName: sanitizedFilename,
        size: file.size,
        mimeType: file.type,
        uploadedAt: new Date().toISOString(),
        uploadedBy: userId,
        tripId: tripId,
        userId: userId,
        storagePath: storagePath,
        storageId: storageId,
        downloadUrl: downloadUrl,
        categories: [
          {
            id: 'uncategorized',
            confidence: 1.0,
            isPrimary: true
          }
        ],
        primaryCategory: 'uncategorized',
        summary: options.summary || null,
        url: options.url || null,
        preview: {
          type: 'placeholder',
          content: null,
          format: null,
          generatedAt: null,
          confidence: null
        },
        connections: options.connections || [],
        extractedData: {
          documentType: null,
          confidence: null,
          data: {},
          extractedAt: null
        },
        processingStatus: 'uploading',
        processingError: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Store document metadata in Firestore with Firestore-generated ID
      const documentsRef = collection(db, 'users', userId, 'trips', tripId, 'documents');
      const docRef = await addDoc(documentsRef, {
        ...documentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const finalDocument = {
        ...documentData,
        id: docRef.id,
        firestoreId: docRef.id
      };

      // Verify document was created before returning (helps prevent race conditions)
      console.log(`âœ… Document created with ID: ${docRef.id}`);

      // Wait a moment to ensure Firestore propagation
      await new Promise(resolve => setTimeout(resolve, 100));

      eventBridgeService.publish(EVENT_TYPES.DOCUMENT_UPLOADED, {
        userId,
        tripId,
        documentId: docRef.id,
        documentData: finalDocument,
        timestamp: new Date().toISOString()
      });

      return finalDocument;
    } catch (error) {
      console.error('Error creating document:', error);
      throw new Error(`Failed to create document: ${error.message}`);
    }
  },

  /**
   * Retrieves all documents for a trip
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @returns {Promise<Array>} Array of document objects
   */
  async fetchDocuments(userId, tripId) {
    try {
      // Ensure Firebase is initialized
      ensureFirebaseInitialized();

      console.log('DocumentService - db object:', db);
      console.log('DocumentService - db type:', typeof db);
      console.log('DocumentService - userId:', userId, 'tripId:', tripId);

      const documentsRef = collection(db, 'users', userId, 'trips', tripId, 'documents');
      console.log('DocumentService - documentsRef:', documentsRef);

      // Try to get documents, but handle the case where collection doesn't exist yet
      try {
        const q = query(documentsRef, orderBy('uploadedAt', 'desc'));
        const querySnapshot = await getDocs(q);

        console.log('DocumentService - querySnapshot:', querySnapshot);
        console.log('DocumentService - docs count:', querySnapshot.docs.length);

        return querySnapshot.docs.map(doc => ({
          firestoreId: doc.id,
          ...doc.data()
        }));
      } catch (collectionError) {
        console.log('DocumentService - Collection might not exist yet:', collectionError.message);
        // If collection doesn't exist, return empty array
        return [];
      }
    } catch (error) {
      console.error('Error getting documents:', error);
      throw new Error(`Failed to get documents: ${error.message}`);
    }
  },

  /**
   * Retrieves a specific document by ID
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} documentId - The ID of the document
   * @returns {Promise<Object|null>} Document object or null if not found
   */
  async getDocument(userId, tripId, documentId) {
    try {
      const documentRef = doc(db, 'users', userId, 'trips', tripId, 'documents', documentId);
      const documentDoc = await getDoc(documentRef);

      if (!documentDoc.exists()) {
        return null;
      }

      return {
        id: documentDoc.id,
        firestoreId: documentDoc.id,
        ...documentDoc.data()
      };
    } catch (error) {
      console.error('Error getting document:', error);
      throw new Error(`Failed to get document: ${error.message}`);
    }
  },

  /**
   * Updates document metadata
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} documentId - The ID of the document
   * @param {Object} updateData - The data to update
   * @returns {Promise<void>}
   */
  async updateDocument(userId, tripId, documentId, updateData) {
    try {
      const documentRef = doc(db, 'users', userId, 'trips', tripId, 'documents', documentId);

      // Add retry mechanism for newly created documents
      let retries = 3;
      let lastError;

      while (retries > 0) {
        try {
          await updateDoc(documentRef, {
            ...updateData,
            updatedAt: serverTimestamp()
          });

          // Success, break out of retry loop
          break;
        } catch (error) {
          lastError = error;

          if (error.code === 'not-found' && retries > 1) {
            console.log(`ðŸ”„ Document not found, retrying... (${4 - retries}/3) - waiting 500ms`);
            // Wait a bit longer before retrying for newly created documents - v2
            await new Promise(resolve => setTimeout(resolve, 500));
            retries--;
            continue;
          }

          // If it's not a not-found error, or we're out of retries, throw
          throw error;
        }
      }

      if (retries === 0) {
        throw lastError;
      }

      // Publish document updated event
      eventBridgeService.publish(EVENT_TYPES.DOCUMENT_UPDATED, {
        userId,
        tripId,
        documentId,
        updateData,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating document:', error);
      throw new Error(`Failed to update document: ${error.message}`);
    }
  },

  /**
   * Deletes a document and its associated file
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} documentId - The ID of the document
   * @returns {Promise<void>}
   */
  async deleteDocument(userId, tripId, documentId) {
    try {
      ensureFirebaseInitialized();
      // Fetch the document to get the storagePath
      const documentRef = doc(db, 'users', userId, 'trips', tripId, 'documents', documentId);
      const docSnap = await getDoc(documentRef);
      if (!docSnap.exists()) {
        throw new Error('Document not found');
      }
      const docData = docSnap.data();
      const storagePath = docData.storagePath;
      if (storagePath) {
        const storageRef = ref(storage, storagePath);
        try {
          await deleteObject(storageRef);
        } catch (e) {
          // If file not found, ignore or log
          console.warn('Storage file not found or already deleted:', e.message);
        }
      }
      await deleteDocFS(documentRef);
      eventBridgeService.publish(EVENT_TYPES.DOCUMENT_DELETED, {
        userId,
        tripId,
        documentId,
        timestamp: new Date().toISOString()
      });
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw new Error(`Failed to delete document: ${error.message}`);
    }
  },

  /**
   * Updates document processing status
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} documentId - The ID of the document
   * @param {string} status - The processing status
   * @param {string|null} error - Error message if status is 'failed'
   * @returns {Promise<void>}
   */
  async updateProcessingStatus(userId, tripId, documentId, status, error = null) {
    try {
      const updateData = {
        processingStatus: status,
        processingError: error,
        updatedAt: serverTimestamp()
      };

      await this.updateDocument(userId, tripId, documentId, updateData);

      // Publish processing status event
      if (status === 'completed') {
        eventBridgeService.publish(EVENT_TYPES.DOCUMENT_PROCESSED, {
          userId,
          tripId,
          documentId,
          status,
          timestamp: new Date().toISOString()
        });
      } else if (status === 'failed') {
        eventBridgeService.publish(EVENT_TYPES.DOCUMENT_PROCESSING_FAILED, {
          userId,
          tripId,
          documentId,
          error,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error updating processing status:', error);
      throw new Error(`Failed to update processing status: ${error.message}`);
    }
  },

  /**
   * Updates document categories and AI processing results
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} documentId - The ID of the document
   * @param {Object} aiResults - AI processing results
   * @returns {Promise<void>}
   */
  async updateAIResults(userId, tripId, documentId, aiResults) {
    try {
      const updateData = {
        categories: aiResults.categories || [],
        primaryCategory: aiResults.primaryCategory || 'uncategorized',
        summary: aiResults.summary || null,
        preview: aiResults.preview || null,
        extractedData: aiResults.extractedData || null,
        processingStatus: 'completed',
        processingError: null,
        updatedAt: serverTimestamp()
      };

      await this.updateDocument(userId, tripId, documentId, updateData);
    } catch (error) {
      console.error('Error updating AI results:', error);
      throw new Error(`Failed to update AI results: ${error.message}`);
    }
  },

  /**
   * Updates an entity's contentBlocks to include a document reference
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} entityType - The type of entity (day, accommodation, place, travel)
   * @param {string} entityId - The ID of the entity
   * @param {Object} document - The document object
   * @param {string} connectionType - The type of connection (primary, related)
   * @returns {Promise<void>}
   */
  async addDocumentReferenceToEntity(userId, tripId, entityType, entityId, document, connectionType) {
    try {
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (config.writeMode === 'registry_only') {
        console.log(
          `[addDocumentReferenceToEntity] Skipping legacy document_reference write for ${entityType}/${entityId} (registry_only)`
        );
        return;
      }

      console.log(`ðŸ“Ž [addDocumentReferenceToEntity] START:`, {
        userId,
        tripId,
        entityType,
        entityId,
        documentId: document?.id || document?.firestoreId,
        documentTitle: document?.title || document?.name,
        connectionType
      });

      // Determine the collection based on entity type (including subtypes)
      let entityCollection;
      const entityTypeLower = entityType.toLowerCase();
      switch (entityTypeLower) {
        case 'day':
          entityCollection = 'days';
          break;
        case 'accommodation':
        case 'hotel':
        case 'apartment':
        case 'house_villa':
        case 'resort':
        case 'hostel':
          entityCollection = 'accommodation';
          break;
        case 'place':
          entityCollection = 'places';
          break;
        case 'travel':
        case 'flight':
        case 'train':
        case 'bus':
        case 'car':
        case 'cruise':
        case 'ferry':
          entityCollection = 'travel';
          break;
        default:
          console.warn(`ðŸ“Ž [addDocumentReferenceToEntity] Unknown entity type: ${entityType} - RETURNING EARLY`);
          return;
      }

      console.log(`ðŸ“Ž [addDocumentReferenceToEntity] Resolved collection: ${entityCollection}`);

      // Get the entity document
      const entityRef = doc(db, 'users', userId, 'trips', tripId, entityCollection, entityId);
      const entityDoc = await getDoc(entityRef);

      if (!entityDoc.exists()) {
        console.warn(`ðŸ“Ž [addDocumentReferenceToEntity] âš ï¸ Entity not found: ${entityType}/${entityId} - RETURNING EARLY`);
        return;
      }

      console.log(`ðŸ“Ž [addDocumentReferenceToEntity] Entity found, checking for existing reference`);

      const entityData = entityDoc.data();
      // Days use 'content' field (architectural standard), others use 'contentBlocks'
      const existingContentBlocks = entityCollection === 'days'
        ? (entityData.content || [])
        : (entityData.contentBlocks || []);

      // Get the correct document ID (handles both id and firestoreId formats)
      const docId = document.id || document.firestoreId;

      console.log(`ðŸ“Ž [addDocumentReferenceToEntity] Document ID to use: ${docId}, existing contentBlocks count: ${existingContentBlocks.length}`);

      // Check if document reference already exists
      const existingReference = existingContentBlocks.find(
        block => block.type === 'document_reference' && block.documentId === docId
      );

      if (existingReference) {
        console.log(`ðŸ“Ž [addDocumentReferenceToEntity] Document reference already exists - RETURNING EARLY`);
        return;
      }


      // Create the document reference block
      const documentReferenceBlock = createDocumentReferenceBlock(document, connectionType);

      // Add the new content block
      const updatedContentBlocks = [...existingContentBlocks, documentReferenceBlock];

      console.log(`ðŸ”— Adding document reference to ${entityType}/${entityId}:`, {
        documentTitle: document.title || document.name,
        blockType: documentReferenceBlock.type,
        blockId: documentReferenceBlock.id,
        totalContentBlocks: updatedContentBlocks.length
      });

      // Update the entity with appropriate field name
      const updateData = {
        updatedAt: serverTimestamp()
      };

      // Days use 'content' field (architectural standard), others use 'contentBlocks'
      if (entityCollection === 'days') {
        updateData.content = updatedContentBlocks;
      } else {
        updateData.contentBlocks = updatedContentBlocks;
      }

      await updateDoc(entityRef, updateData);

      console.log(`âœ… Successfully added document reference to ${entityType}/${entityId}`);
    } catch (error) {
      console.error(`Error adding document reference to entity ${entityType}/${entityId}:`, error);
      // Don't throw error - this shouldn't fail the main operation
    }
  },

  /**
   * Removes a document reference from an entity's contentBlocks
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} entityType - The type of entity
   * @param {string} entityId - The ID of the entity
   * @param {string} documentId - The ID of the document
   * @returns {Promise<void>}
   */
  async removeDocumentReferenceFromEntity(userId, tripId, entityType, entityId, documentId) {
    try {
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (config.writeMode === 'registry_only') {
        console.log(
          `[removeDocumentReferenceFromEntity] Skipping legacy document_reference removal for ${entityType}/${entityId} (registry_only)`
        );
        return;
      }

      // Determine the collection based on entity type (including subtypes)
      let entityCollection;
      const entityTypeLower = entityType.toLowerCase();
      switch (entityTypeLower) {
        case 'day':
          entityCollection = 'days';
          break;
        case 'accommodation':
        case 'hotel':
        case 'apartment':
        case 'house_villa':
        case 'resort':
        case 'hostel':
          entityCollection = 'accommodation';
          break;
        case 'place':
          entityCollection = 'places';
          break;
        case 'travel':
        case 'flight':
        case 'train':
        case 'bus':
        case 'car':
        case 'cruise':
        case 'ferry':
          entityCollection = 'travel';
          break;
        default:
          console.warn(`Unknown entity type: ${entityType}`);
          return;
      }

      // Get the entity document
      const entityRef = doc(db, 'users', userId, 'trips', tripId, entityCollection, entityId);
      const entityDoc = await getDoc(entityRef);

      if (!entityDoc.exists()) {
        console.warn(`âš ï¸ Entity not found: ${entityType}/${entityId} - association will be skipped`);
        return;
      }

      const entityData = entityDoc.data();
      // Days use 'content' field (architectural standard), others use 'contentBlocks'
      const existingContentBlocks = entityCollection === 'days'
        ? (entityData.content || [])
        : (entityData.contentBlocks || []);

      // Remove the document reference block
      const updatedContentBlocks = existingContentBlocks.filter(
        block => !(block.type === 'document_reference' && block.documentId === documentId)
      );

      // Update the entity if any blocks were removed
      if (updatedContentBlocks.length !== existingContentBlocks.length) {
        // Days use 'content' field (architectural standard), others use 'contentBlocks'
        const updateData = {
          updatedAt: serverTimestamp()
        };
        if (entityCollection === 'days') {
          updateData.content = updatedContentBlocks;
        } else {
          updateData.contentBlocks = updatedContentBlocks;
        }
        await updateDoc(entityRef, updateData);

        console.log(`Removed document reference from ${entityType}/${entityId}`);
      }
    } catch (error) {
      console.error(`Error removing document reference from entity ${entityType}/${entityId}:`, error);
      // Don't throw error - this shouldn't fail the main operation
    }
  },

  /**
   * Adds a connection between a document and an entity
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} documentId - The ID of the document
   * @param {Object} connection - The connection data
   * @returns {Promise<void>}
   */
  async addConnection(userId, tripId, documentId, connection) {
    try {
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      const registryEntityType = toRegistryEntityType(connection.entityType);

      if (shouldRouteThroughRegistry(config)) {
        const document = await this.getDocument(userId, tripId, documentId);
        if (!document) {
          throw new Error('Document not found');
        }
        const { entityLinkingService } = await import('@trip-planner-monorepo/shared/services/entityLinkingService');
        const result = await entityLinkingService.createLink(
          userId,
          tripId,
          'document',
          documentId,
          registryEntityType,
          connection.entityId,
          { metadata: { connectionType: connection.connectionType || 'related', entityName: connection.entityName } }
        );

        if (!result.alreadyExists) {
          eventBridgeService.publish(EVENT_TYPES.DOCUMENT_ASSOCIATED, {
            userId,
            tripId,
            documentId,
            connection: {
              entityType: connection.entityType,
              entityId: connection.entityId,
              entityName: connection.entityName || 'Unnamed Entity',
              connectionType: connection.connectionType || 'related',
              associatedAt: connection.associatedAt || new Date().toISOString(),
              connectedAt: new Date().toISOString()
            },
            timestamp: new Date().toISOString()
          });
        }
        return;
      }

      console.log(`ðŸ”— addConnection called with documentId: ${documentId}, entityType: ${connection.entityType}, entityId: ${connection.entityId}`);

      const document = await this.getDocument(userId, tripId, documentId);
      console.log(`ðŸ“„ Retrieved document:`, {
        hasDocument: !!document,
        documentId: document?.id,
        firestoreId: document?.firestoreId,
        title: document?.title || document?.name,
        documentKeys: document ? Object.keys(document) : []
      });

      if (!document) {
        throw new Error('Document not found');
      }

      // Check for duplicate connection BEFORE adding
      const existingConnection = (document.connections || []).find(
        conn => conn.entityType === connection.entityType && conn.entityId === connection.entityId
      );

      if (existingConnection) {
        console.log(`âš ï¸ Connection already exists for ${connection.entityType}/${connection.entityId}, skipping duplicate`);
        // Still ensure bidirectional link exists
        await this.addDocumentReferenceToEntity(
          userId,
          tripId,
          connection.entityType,
          connection.entityId,
          document,
          connection.connectionType || 'related'
        );
        return; // Exit early - don't add duplicate
      }

      const newConnection = {
        entityType: connection.entityType,
        entityId: connection.entityId,
        entityName: connection.entityName || 'Unnamed Entity',
        connectionType: connection.connectionType || 'related',
        associatedAt: connection.associatedAt || new Date().toISOString(),
        connectedAt: new Date().toISOString()
      };

      const updatedConnections = [...(document.connections || []), newConnection];

      // Update document with new connection
      await this.updateDocument(userId, tripId, documentId, {
        connections: updatedConnections
      });

      // Add bidirectional relationship: update entity's contentBlocks with document reference
      // Add small delay to allow Firestore propagation for newly created entities
      console.log(`ðŸ”— addConnection: Adding bidirectional link to ${connection.entityType}/${connection.entityId}`);
      await new Promise(resolve => setTimeout(resolve, 300));

      await this.addDocumentReferenceToEntity(
        userId,
        tripId,
        connection.entityType,
        connection.entityId,
        document,
        connection.connectionType || 'related'
      );

      // Publish document associated event
      eventBridgeService.publish(EVENT_TYPES.DOCUMENT_ASSOCIATED, {
        userId,
        tripId,
        documentId,
        connection: newConnection,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error adding connection:', error);
      throw new Error(`Failed to add connection: ${error.message}`);
    }
  },

  /**
   * Removes a connection between a document and an entity
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} documentId - The ID of the document
   * @param {string} entityType - The type of entity
   * @param {string} entityId - The ID of the entity
   * @returns {Promise<void>}
   */
  async removeConnection(userId, tripId, documentId, entityType, entityId, options = {}) {
    try {
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      const registryEntityType = toRegistryEntityType(entityType);

      if (!options.legacyCleanupOnly && shouldRouteThroughRegistry(config)) {
        const { entityLinkingService } = await import('@trip-planner-monorepo/shared/services/entityLinkingService');
        await entityLinkingService.removeLink(
          userId,
          tripId,
          'document',
          documentId,
          registryEntityType,
          entityId
        );

        eventBridgeService.publish(EVENT_TYPES.DOCUMENT_DISASSOCIATED, {
          userId,
          tripId,
          documentId,
          entityType,
          entityId,
          timestamp: new Date().toISOString()
        });
        return;
      }

      const document = await this.getDocument(userId, tripId, documentId);
      if (!document) {
        throw new Error('Document not found');
      }

      if (config.writeMode !== 'registry_only') {
        const updatedConnections = (document.connections || []).filter(
          (conn) => !(
            conn.entityId === entityId
            && toRegistryEntityType(conn.entityType) === registryEntityType
          )
        );

        await this.updateDocument(userId, tripId, documentId, {
          connections: updatedConnections
        });

        if (!options.skipBidirectionalRemoval) {
          await this.removeDocumentReferenceFromEntity(
            userId,
            tripId,
            entityType,
            entityId,
            documentId
          );
        }
      }

      if (!options.suppressEvent) {
        eventBridgeService.publish(EVENT_TYPES.DOCUMENT_DISASSOCIATED, {
          userId,
          tripId,
          documentId,
          entityType,
          entityId,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error removing connection:', error);
      throw new Error(`Failed to remove connection: ${error.message}`);
    }
  },

  /**
   * Syncs document_for registry links to match the selected association set (registry trips).
   * Uses getAllLinks (not participantIds query) so archived rows without participantIds are handled.
   */
  async syncRegistryDocumentAssociations(userId, tripId, documentId, selectedAssociations = []) {
    const { connectionRegistryService } = await import(
      '@trip-planner-monorepo/shared/services/connectionRegistryService'
    );
    const { entityLinkingService } = await import(
      '@trip-planner-monorepo/shared/services/entityLinkingService'
    );

    const allDocForLinks = (
      await connectionRegistryService.getAllLinks(userId, tripId, {
        includeArchived: true,
        linkType: LINK_TYPES.DOCUMENT_FOR
      })
    ).filter((link) => {
      const docEndpoint = [link.entityLow, link.entityHigh].find((e) => e?.type === 'document');
      return docEndpoint?.id === documentId;
    });

    const selectedKeys = new Set(
      selectedAssociations.map((a) => `${toRegistryEntityType(a.entityType)}:${a.entityId}`)
    );

    const activeKeys = new Set();

    for (const link of allDocForLinks) {
      const otherEndpoint =
        link.entityLow?.type === 'document' ? link.entityHigh : link.entityLow;
      if (!otherEndpoint?.type || !otherEndpoint?.id) {
        continue;
      }
      const key = `${toRegistryEntityType(otherEndpoint.type)}:${otherEndpoint.id}`;

      if (isActiveLink(link)) {
        activeKeys.add(key);
      }

      if (!selectedKeys.has(key)) {
        await entityLinkingService.removeLink(
          userId,
          tripId,
          'document',
          documentId,
          otherEndpoint.type,
          otherEndpoint.id
        );
      }
    }

    for (const association of selectedAssociations) {
      const key = `${toRegistryEntityType(association.entityType)}:${association.entityId}`;
      if (!activeKeys.has(key)) {
        await this.addConnection(userId, tripId, documentId, {
          entityType: association.entityType,
          entityId: association.entityId,
          entityName: association.entityName,
          connectionType: association.connectionType || 'related',
          associatedAt: association.associatedAt || new Date().toISOString()
        });
      }
    }
  },

  /**
   * Clears all connections for a document
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} documentId - The ID of the document
   * @returns {Promise<void>}
   */
  async clearConnections(userId, tripId, documentId) {
    try {
      const document = await this.getDocument(userId, tripId, documentId);
      if (!document) {
        throw new Error('Document not found');
      }

      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      const existingConnections = [...(document.connections || [])];
      let clearedCount = 0;

      if (shouldRouteThroughRegistry(config)) {
        const { connectionRegistryService } = await import(
          '@trip-planner-monorepo/shared/services/connectionRegistryService'
        );
        const { legacyLinkAdapter } = await import(
          '@trip-planner-monorepo/shared/services/legacyLinkAdapter'
        );

        // Hard-delete all document_for rows (including archived) so replace-all saves
        // do not stack duplicate dedupKey documents when links are re-added.
        const registryLinks = (
          await connectionRegistryService.getAllLinks(userId, tripId, {
            includeArchived: true,
            linkType: LINK_TYPES.DOCUMENT_FOR
          })
        ).filter((link) => {
          const docEndpoint = [link.entityLow, link.entityHigh].find((e) => e?.type === 'document');
          return docEndpoint?.id === documentId;
        });

        for (const link of registryLinks) {
          const linkId = link.linkId || link.id;
          if (!linkId) {
            continue;
          }
          await connectionRegistryService.removeLink(userId, tripId, linkId, { hardDelete: true });
          if (config.writeMode === 'registry_only') {
            await legacyLinkAdapter.stripLegacyShadow(userId, tripId, link);
          }
          clearedCount += 1;
        }
      }

      if (config.writeMode !== 'registry_only') {
        for (const connection of existingConnections) {
          const { entityType, entityId } = connection;
          if (!entityType || !entityId) {
            continue;
          }

          await this.removeConnection(userId, tripId, documentId, entityType, entityId, {
            legacyCleanupOnly: true,
            suppressEvent: true
          });
        }

        await this.updateDocument(userId, tripId, documentId, {
          connections: []
        });
        clearedCount = Math.max(clearedCount, existingConnections.length);
      }

      eventBridgeService.publish(EVENT_TYPES.DOCUMENT_DISASSOCIATED, {
        userId,
        tripId,
        documentId,
        action: 'cleared_all',
        clearedCount,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error clearing connections:', error);
      throw new Error(`Failed to clear connections: ${error.message}`);
    }
  },

  /**
   * Searches documents by various criteria
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {Object} searchCriteria - Search criteria
   * @returns {Promise<Array>} Array of matching documents
   */
  async searchDocuments(userId, tripId, searchCriteria = {}) {
    try {
      const documentsRef = collection(db, 'users', userId, 'trips', tripId, 'documents');
      let q = query(documentsRef, orderBy('uploadedAt', 'desc'));

      // Add category filter
      if (searchCriteria.category) {
        q = query(q, where('primaryCategory', '==', searchCriteria.category));
      }

      // Add processing status filter
      if (searchCriteria.processingStatus) {
        q = query(q, where('processingStatus', '==', searchCriteria.processingStatus));
      }

      const querySnapshot = await getDocs(q);
      let documents = querySnapshot.docs.map(doc => ({
        firestoreId: doc.id,
        ...doc.data()
      }));

      // Apply text search filter
      if (searchCriteria.searchTerm) {
        const searchTerm = searchCriteria.searchTerm.toLowerCase();
        documents = documents.filter(doc =>
          doc.title?.toLowerCase().includes(searchTerm) ||
          doc.name?.toLowerCase().includes(searchTerm) ||
          doc.summary?.toLowerCase().includes(searchTerm)
        );
      }

      return documents;
    } catch (error) {
      console.error('Error searching documents:', error);
      throw new Error(`Failed to search documents: ${error.message}`);
    }
  },

  /**
   * Gets documents by category
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} categoryId - The category ID
   * @returns {Promise<Array>} Array of documents in the category
   */
  async getDocumentsByCategory(userId, tripId, categoryId) {
    try {
      const documents = await this.fetchDocuments(userId, tripId);
      return documents.filter(doc =>
        doc.categories?.some(cat => cat.id === categoryId)
      );
    } catch (error) {
      console.error('Error getting documents by category:', error);
      throw new Error(`Failed to get documents by category: ${error.message}`);
    }
  },

  /**
   * Gets documents connected to a specific entity
   * @param {string} userId - The ID of the user
   * @param {string} tripId - The ID of the trip
   * @param {string} entityType - The type of entity
   * @param {string} entityId - The ID of the entity
   * @returns {Promise<Array>} Array of connected documents
   */
  async getDocumentsByEntity(userId, tripId, entityType, entityId) {
    try {
      const documents = await this.fetchDocuments(userId, tripId);
      return documents.filter(doc =>
        doc.connections?.some(conn =>
          conn.entityType === entityType && conn.entityId === entityId
        )
      );
    } catch (error) {
      console.error('Error getting documents by entity:', error);
      throw new Error(`Failed to get documents by entity: ${error.message}`);
    }
  },

  /**
   * Initializes the document service and sets up event listeners
   */
  init() {
    console.log('ðŸ“„ [documentService] Initializing event listeners...');

    // Subscribe to entity deletion events for automatic cleanup
    eventBridgeService.subscribe(EVENT_TYPES.TRAVEL_DELETED, (payload) => this.handleEntityDeleted(payload, 'travel'));
    eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_DELETED, (payload) => this.handleEntityDeleted(payload, 'accommodation'));
    eventBridgeService.subscribe(EVENT_TYPES.PLACE_DELETED, (payload) => this.handleEntityDeleted(payload, 'place'));
    eventBridgeService.subscribe(EVENT_TYPES.DAY_DELETED, (payload) => this.handleEntityDeleted(payload, 'day'));

    console.log('ðŸ“„ [documentService] Subscribed to entity deletion events');
  },

  /**
   * Handles entity deletion by removing all document connections to that entity
   * @param {Object} payload - Event payload (must contain userId, tripId, and id)
   * @param {string} entityType - The type of entity that was deleted
   */
  async handleEntityDeleted(payload, entityType) {
    const { userId, tripId, id: entityId } = payload;

    if (!userId || !tripId || !entityId) {
      console.warn(`ðŸ“„ [documentService] handleEntityDeleted: Missing data in payload`, { payload, entityType });
      return;
    }

    console.log(`ðŸ“„ [documentService] Cleaning up document connections for deleted ${entityType}: ${entityId}`);

    try {
      // Find all documents connected to this entity
      const connectedDocuments = await this.getDocumentsByEntity(userId, tripId, entityType, entityId);

      if (connectedDocuments.length === 0) {
        console.log(`ðŸ“„ [documentService] No documents found connected to ${entityType}: ${entityId}`);
        return;
      }

      console.log(`ðŸ“„ [documentService] Found ${connectedDocuments.length} documents to update`);

      // Remove the connection from each document
      for (const document of connectedDocuments) {
        const documentId = document.firestoreId || document.id;

        // Use a flag to avoid trying to remove bidirectional reference from a deleted entity
        await this.removeConnection(userId, tripId, documentId, entityType, entityId, { skipBidirectionalRemoval: true });
      }

      console.log(`ðŸ“„ [documentService] Successfully cleaned up ${connectedDocuments.length} document connections`);
    } catch (error) {
      console.error(`ðŸ“„ [documentService] Error during entity deletion cleanup:`, error);
    }
  }
};